<!-- The compact call view (ITP-E-185-01): Meet's picture-in-picture as a card, or one slim strip. -->
<section class="compact-call-view" role="region" [attr.aria-label]="ariaLabel" [ngClass]="'compact-call-view-' + view.mode"
         [class.compact-call-view-large]="view.size === 'large'" [class.compact-call-view-full]="view.size === 'full'"
         [class.compact-call-view-sharing]="!!view.sharer">
    <!-- Their screen, in a canvas of the stage's own as Meet's participant-screen-share-canvas (ITP-E-189-05), their name
         bottom-left as Meet's presenter name is. One canvas for the view's life (ITP-E-189-07), kept and only hidden as Meet
         keeps its own: the SDK takes a canvas over and goes on drawing into the one it first got, so a sharer, size or mode
         change never creates another. Its place under the header is the card's flex order. -->
    <div class="compact-call-view-share-stage" [hidden]="!showsShare" [attr.aria-hidden]="!showsShare">
        <canvas class="compact-call-view-share-canvas" #shareCanvas data-slot="share" role="img"
                [attr.aria-label]="(view.sharer?.name || 'Shared') + '\'s screen'"></canvas>
        <span class="compact-call-view-name" *ngIf="showsShare">{{ view.sharer?.name }}</span>
    </div>
    <ng-container *ngIf="view.mode === 'card'; else strip">
        <!-- The host's own header: its handle and its sizes. -->
        <ng-content select="[compactCallHeader]"></ng-content>
        <!-- This side's own share is not drawn back to it: no hall of mirrors. "You are sharing · Stop" (ITP-E-185-04). -->
        <div class="compact-call-view-share-stage compact-call-view-own-share" *ngIf="view.sharer?.self">
            <i [ngClass]="icons.share" aria-hidden="true"></i>
            <span>You are sharing</span>
            <button type="button" class="compact-call-view-stop-share" (click)="onToggleShare()" aria-label="Stop sharing"
                    title="Stop sharing">
                Stop
            </button>
        </div>
        <!-- The SDK draws only inside a `video-player-container`: the speaker on the stage, this page's thumbnails below. -->
        <video-player-container class="compact-call-view-body">
            <div class="compact-call-view-stage" *ngIf="!view.sharer">
                <div class="compact-call-view-slot" #videoSlot data-slot="stage">
                    <div class="compact-call-view-avatar" *ngIf="showsInitials('stage', view.stage?.video)" role="img" [attr.aria-label]="stageName || 'No video'">
                        <span>{{ stageInitials }}</span>
                    </div>
                </div>
                <span class="compact-call-view-name" *ngIf="stageName">
                    <i class="fa-solid fa-microphone-slash" *ngIf="view.stage?.muted" aria-label="Muted" role="img"></i>
                    {{ stageName }}
                </span>
            </div>
            <div class="compact-call-view-thumbnails" *ngIf="thumbnails.tiles.length" role="list" aria-label="Participants">
                <button type="button" class="compact-call-view-page compact-call-view-page-previous" *ngIf="thumbnails.pages > 1"
                        [disabled]="thumbnails.page === 0" (click)="onPage(-1)" aria-label="Previous participants"
                        title="Previous participants">
                    <i [ngClass]="icons.previous" aria-hidden="true"></i>
                </button>
                <div class="compact-call-view-tile" *ngFor="let tile of thumbnails.tiles; trackBy: trackPerson" role="listitem"
                     [attr.data-identity]="tile.identity" [class.compact-call-view-tile-speaking]="tile.speaking"
                     [attr.aria-label]="tile.name + (tile.muted ? ', muted' : '') + (tile.speaking ? ', speaking' : '')">
                    <div class="compact-call-view-slot" #videoSlot [attr.data-slot]="'tile:' + tile.identity">
                        <span class="compact-call-view-tile-avatar" *ngIf="showsInitials('tile:' + tile.identity, tile.video)" aria-hidden="true">{{ tile.initials }}</span>
                    </div>
                    <span class="compact-call-view-tile-name" aria-hidden="true">
                        <i class="fa-solid fa-microphone-slash" *ngIf="tile.muted"></i>
                        {{ tile.name }}
                    </span>
                </div>
                <button type="button" class="compact-call-view-page compact-call-view-page-next" *ngIf="thumbnails.pages > 1"
                        [disabled]="thumbnails.page >= thumbnails.pages - 1" (click)="onPage(1)" aria-label="Next participants"
                        title="Next participants">
                    <i [ngClass]="icons.next" aria-hidden="true"></i>
                </button>
            </div>
        </video-player-container>
        <ng-container *ngTemplateOutlet="panels"></ng-container>
        <!-- Meet's PiP footer: square icon buttons, End call last. -->
        <footer class="compact-call-view-footer">
            <ng-container *ngTemplateOutlet="controls"></ng-container>
        </footer>
    </ng-container>

    <!-- Carbon's in-call mini bar (ITP-E-191-03): the face — the speaker's thumbnail in a video call — the host's handle (else
         the name over "mm:ss · N in call"), the controls as circles, Open call, and Expand where the host offers it. -->
    <ng-template #strip>
        <video-player-container class="compact-call-view-mini" *ngIf="view.video">
            <div class="compact-call-view-slot" #videoSlot data-slot="mini">
                <span class="compact-call-view-tile-avatar" *ngIf="showsInitials('mini', view.stage?.video)" role="img" [attr.aria-label]="stageName || 'No video'">
                    {{ stageInitials }}
                </span>
            </div>
        </video-player-container>
        <span class="compact-call-view-face" *ngIf="!view.video" [attr.data-tone]="faceTone" aria-hidden="true">{{ faceInitials }}</span>
        <span class="compact-call-view-title">
            <ng-content select="[compactCallTitle]">
                <span class="compact-call-view-title-name">{{ view.title }}</span>
                <!-- Not a live region: it would announce every second. -->
                <span class="compact-call-view-title-meta">
                    <span class="compact-call-view-title-timer" role="timer" aria-live="off">{{ view.timer }}</span>{{ inCallLabel }}
                </span>
            </ng-content>
        </span>
        <ng-container *ngTemplateOutlet="controls"></ng-container>
        <!-- Carbon's tertiary Open call: this call, in Meet, in a window the click opens (ITP-E-182-05). -->
        <button type="button" class="compact-call-view-open-call" *ngIf="view.options.openInMeet" (click)="onOpenInMeet()"
                [disabled]="!view.options.meetReady" aria-label="Open call in Meet"
                title="Open this call in Meet in a new tab (leaves the call here)">
            Open call
        </button>
        <button type="button" class="compact-call-view-button compact-call-view-expand" *ngIf="view.expandable" (click)="onExpand()"
                aria-label="Expand" title="Expand">
            <i [ngClass]="icons.expand" aria-hidden="true"></i>
        </button>
        <ng-container *ngTemplateOutlet="panels"></ng-container>
    </ng-template>
</section>

<!-- Mute, Camera, Share (the card's), ⋯ and End call: Meet's order, the red one last. Square on the card, circles on the
     strip; off, a toggle shows its slashed glyph as well as its pressed ground. -->
<ng-template #controls>
    <button type="button" class="compact-call-view-button compact-call-view-mute" (click)="onToggleMute()"
            [disabled]="!view.self.connected" [class.compact-call-view-button-off]="view.self.muted"
            [attr.aria-pressed]="view.self.muted" [attr.aria-label]="view.self.muted ? 'Unmute' : 'Mute'"
            [title]="view.self.muted ? 'Turn on microphone' : 'Turn off microphone'">
        <i [ngClass]="view.self.muted ? icons.microphoneOff : icons.microphone" aria-hidden="true"></i>
    </button>
    <button type="button" class="compact-call-view-button compact-call-view-camera" (click)="onToggleCamera()"
            [disabled]="!view.self.connected" [class.compact-call-view-button-off]="!view.self.videoOn"
            [attr.aria-pressed]="view.self.videoOn" aria-label="Camera"
            [title]="view.self.videoOn ? 'Turn off camera' : 'Turn on camera'">
        <i [ngClass]="view.self.videoOn ? icons.camera : icons.cameraOff" aria-hidden="true"></i>
    </button>
    <button type="button" class="compact-call-view-button compact-call-view-share" *ngIf="view.mode === 'card' && view.self.canShare"
            (click)="onToggleShare()" [disabled]="!view.self.connected" [class.compact-call-view-button-active]="view.self.sharing"
            [attr.aria-pressed]="view.self.sharing" aria-label="Share screen"
            [title]="view.self.sharing ? 'Stop presenting' : 'Present now'">
        <i [ngClass]="icons.share" aria-hidden="true"></i>
    </button>
    <span class="compact-call-view-options">
        <button type="button" class="compact-call-view-button compact-call-view-options-toggle" (click)="onToggleMenu()"
                [class.compact-call-view-button-active]="menuOpen" aria-label="More options" title="More options"
                aria-haspopup="menu" [attr.aria-expanded]="menuOpen">
            <i [ngClass]="icons.options" aria-hidden="true"></i>
        </button>
        <div class="compact-call-view-menu" role="menu" aria-label="More options" *ngIf="menuOpen && !confirmingEndForAll">
            <button type="button" class="compact-call-view-menu-item" role="menuitem" *ngIf="view.participants.length"
                    (click)="onPanel('participants')" [attr.aria-expanded]="panel === 'participants'">
                <i [ngClass]="icons.participants" aria-hidden="true"></i>
                <span class="compact-call-view-menu-label">Participants</span>
            </button>
            <!-- Add by phone (ITP-E-198-01): only where this user may and the call's account dials out. -->
            <button type="button" class="compact-call-view-menu-item" role="menuitem" *ngIf="canAddPhone"
                    (click)="onPanel('phone')" [disabled]="!view.self.connected" [attr.aria-expanded]="panel === 'phone'">
                <i [ngClass]="icons.addPhone" aria-hidden="true"></i>
                <span class="compact-call-view-menu-label">Add by phone</span>
            </button>
            <button type="button" class="compact-call-view-menu-item" role="menuitem" (click)="onPanel('devices')"
                    [disabled]="!view.self.connected" [attr.aria-expanded]="panel === 'devices'">
                <i [ngClass]="icons.devices" aria-hidden="true"></i>
                <span class="compact-call-view-menu-label">Devices</span>
            </button>
            <button type="button" class="compact-call-view-menu-item" role="menuitem" *ngIf="shareInMenu"
                    (click)="onToggleShare()" [disabled]="!view.self.connected">
                <i [ngClass]="icons.share" aria-hidden="true"></i>
                <span class="compact-call-view-menu-label">{{ view.self.sharing ? 'Stop sharing' : 'Share screen' }}</span>
            </button>
            <!-- The host's Record, and Stop recording while it is requested or running (ITP-E-189-06). -->
            <button type="button" class="compact-call-view-menu-item" role="menuitem" *ngIf="view.options.record && !view.options.recording"
                    (click)="onRecord()" [disabled]="!view.self.connected || !!view.options.recordHeldBack"
                    [title]="view.options.recordHeldBack || 'Everyone must allow it'">
                <i [ngClass]="icons.record" aria-hidden="true"></i>
                <span class="compact-call-view-menu-label">Record</span>
            </button>
            <button type="button" class="compact-call-view-menu-item" role="menuitem" *ngIf="view.options.record && view.options.recording"
                    (click)="onStopRecording()" [disabled]="!view.self.connected" title="Stops the recording for everyone">
                <i [ngClass]="icons.stopRecording" aria-hidden="true"></i>
                <span class="compact-call-view-menu-label">Stop recording</span>
            </button>
            <!-- The card's; the strip has its Open call button (ITP-E-191-03). -->
            <button type="button" class="compact-call-view-menu-item" role="menuitem" *ngIf="openInMeetInMenu"
                    (click)="onOpenInMeet()" [disabled]="!view.options.meetReady"
                    title="Open the full Meet room in a new tab (leaves this call)">
                <i [ngClass]="icons.openInMeet" aria-hidden="true"></i>
                <span class="compact-call-view-menu-label">Open in Meet</span>
            </button>
            <button type="button" class="compact-call-view-menu-item compact-call-view-menu-danger" role="menuitem"
                    *ngIf="view.options.endForAll" (click)="onEndForAll()">
                <i [ngClass]="icons.hangUp" aria-hidden="true"></i>
                <span class="compact-call-view-menu-label">End call for everyone</span>
            </button>
        </div>
        <!-- Ending it for everyone cannot be undone, so it asks first. -->
        <div class="compact-call-view-menu compact-call-view-confirm" role="alertdialog" aria-label="End call for everyone?"
             *ngIf="menuOpen && confirmingEndForAll">
            <div class="compact-call-view-confirm-title">End call for everyone?</div>
            <div class="compact-call-view-confirm-text">Everyone leaves the call.</div>
            <div class="compact-call-view-confirm-actions">
                <button type="button" class="compact-call-view-confirm-cancel" (click)="closeMenu()" aria-label="Cancel">Cancel</button>
                <button type="button" class="compact-call-view-confirm-end" (click)="onConfirmEndForAll()"
                        aria-label="End for everyone">End for everyone</button>
            </div>
        </div>
    </span>
    <!-- End call in every call, like WhatsApp (ITP-E-182-04): a group call goes on without whoever ends it. -->
    <button type="button" class="compact-call-view-button compact-call-view-hang-up" (click)="onHangUp()"
            aria-label="End call" [title]="view.hangUpTitle">
        <i [ngClass]="icons.hangUp" aria-hidden="true"></i>
    </button>
</ng-template>

<!-- What ⋯ opened: the group's Participants, the microphone and speaker, or Add by phone (ITP-E-198-01). Each closes on its ×, a click outside it, Esc,
     or choosing its ⋯ item again (ITP-E-189-09). -->
<ng-template #panels>
    <div class="compact-call-view-panel" *ngIf="panel === 'participants' && view.participants.length">
        <app-call-participants-list class="compact-call-view-participants" [rows]="view.participants" [title]="view.title"
                                    listId="compact-call-view-participants" (close)="onClosePanel()"
                                    (hangUpPhone)="onHangUpPhone($event)">
        </app-call-participants-list>
    </div>
    <div class="compact-call-view-panel compact-call-view-phone" *ngIf="panel === 'phone' && canAddPhone">
        <app-phone-invite-dialog [countries]="view.phone!.countries" (call)="onCallPhone($event)" (close)="onClosePanel()">
        </app-phone-invite-dialog>
    </div>
    <div class="compact-call-view-panel compact-call-view-devices" *ngIf="panel === 'devices'" role="dialog"
         aria-label="Microphone and speaker">
        <div class="compact-call-view-devices-header">
            <span class="compact-call-view-devices-title">Microphone and speaker</span>
            <button type="button" class="compact-call-view-panel-close" (click)="onClosePanel()" aria-label="Close devices"
                    title="Close">
                <i class="fa-solid fa-xmark" aria-hidden="true"></i>
            </button>
        </div>
        <!-- The device in use carries Meet's check (ITP-E-189-12), as Meet's device menus mark theirs; the host marks what the
             media reports, so a pick shows once the SDK switched. -->
        <div class="compact-call-view-devices-heading" id="compact-call-view-microphones">Microphone</div>
        <div class="compact-call-view-devices-group" role="group" aria-labelledby="compact-call-view-microphones">
            <button type="button" class="compact-call-view-devices-option" role="menuitemradio"
                    *ngFor="let device of view.devices.list?.microphones"
                    [class.compact-call-view-devices-option-on]="device.deviceId === view.devices.microphoneId"
                    [attr.aria-checked]="device.deviceId === view.devices.microphoneId"
                    (click)="onSelectMicrophone(device.deviceId)">
                <i class="fa-solid fa-check compact-call-view-devices-check" aria-hidden="true"></i>
                <span class="compact-call-view-devices-label">{{ device.label || 'Microphone' }}</span>
            </button>
        </div>
        <div class="compact-call-view-devices-heading" id="compact-call-view-speakers">Speaker</div>
        <div class="compact-call-view-devices-group" role="group" aria-labelledby="compact-call-view-speakers">
            <button type="button" class="compact-call-view-devices-option" role="menuitemradio"
                    *ngFor="let device of view.devices.list?.speakers"
                    [class.compact-call-view-devices-option-on]="device.deviceId === view.devices.speakerId"
                    [attr.aria-checked]="device.deviceId === view.devices.speakerId"
                    (click)="onSelectSpeaker(device.deviceId)">
                <i class="fa-solid fa-check compact-call-view-devices-check" aria-hidden="true"></i>
                <span class="compact-call-view-devices-label">{{ device.label || 'Speaker' }}</span>
            </button>
        </div>
        <div class="compact-call-view-devices-empty" *ngIf="view.devices.list && !view.devices.list.speakers.length">
            This browser plays through the system speaker.
        </div>
    </div>
</ng-template>
